import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { Button, Card, CardContent, Checkbox, FormControlLabel, FormGroup, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import './sales-order-display.css';



const SalesOrderDisplay = props => {
    const [salesOrderItems, setSalesOrderItems] = useState([]);
    const [printHangTags, setPrintHangTags] = useState(true);
    const [printAsinLabels, setPrintAsinLabels] = useState(props.salesOrder.company.toLowerCase().includes("amazon"));
    const [printChewyLabels, setPrintChewyLabels] = useState(props.salesOrder.company.toLowerCase().includes("chewy"));
    const [printWalmartLabels, setPrintWalmartLabels] = useState(props.salesOrder.company.toLowerCase().includes("walmart"));

    useEffect(() => {
        const mappedItems = props.salesOrder.items.map((item, index) => ({
            ...item,
            id: index
        }));
        setSalesOrderItems(mappedItems);
    }, [props.salesOrder]);

    const updateItemQuantity = (itemIndex, newQuantity) => {
        const newSalesOrder = JSON.parse(JSON.stringify(props.salesOrder));
        newSalesOrder.items[itemIndex].qty = newQuantity;
        props.setSalesOrder(newSalesOrder);        
    };

    const zeroAllQuantities = () => {
        const newSalesOrder = JSON.parse(JSON.stringify(props.salesOrder));
        newSalesOrder.items.forEach(item => item.qty = 0);
        props.setSalesOrder(newSalesOrder);
    };

    const print = () => {
        if (!printHangTags && !printAsinLabels && !printChewyLabels && !printWalmartLabels) {
            props.toast.error("Please select a type of label to print");
            return;
        }

        const printOptions = {
            printHangTags,
            printAsinLabels,
            printChewyLabels,
            printWalmartLabels
        };
        
        props.printSalesOrder(printOptions);
    };

    const defaultColumnAttributes = {
        flex: 1,
        editable: false,
        minWidth: 100,
        maxWidth: 200
    };

    const itemTableColumns = [{
        ...defaultColumnAttributes,
        field: "sku",
        headerName: "SKU",
        maxWidth: 150
    }, {
        ...defaultColumnAttributes,
        field: "qty",
        headerName: "Quantity (editable)",
        maxWidth: 150,
        editable: true,
        valueParser: (value, event) => {
            const itemIndex = event.row.id;
            if (value && !isNaN(value)) {
                const newQuantity = parseInt(value);
                updateItemQuantity(itemIndex, newQuantity);
                return newQuantity;
            } else {
                updateItemQuantity(itemIndex, value);
                return value;
            }
        }
    }, {
        ...defaultColumnAttributes,
        field: "size",
        headerName: "Size",
        maxWidth: 100
    }, {
        ...defaultColumnAttributes,
        field: "color",
        headerName: "Color",
        maxWidth: 120
    }, {
        ...defaultColumnAttributes,
        field: "upc",
        headerName: "UPC"
    }, {
        ...defaultColumnAttributes,
        field: "asin",
        headerName: "ASIN"
    }, {
        ...defaultColumnAttributes,
        field: "pretagged",
        headerName: "Pretagged",
        maxWidth: 150
    }];

    const listItemStyles = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };

    const listItemTextStyles = {
        mt: -1,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        paddingLeft: 2,
        paddingRight: 2
    };

    const getRowClassName = params => {
        return !params.row.upc ? "red-table-row" : "";
    };

    const enableHangTagPrinting = props.hangTagPrinters.length;
    const enableAsinLabelPrinting = props.asinLabelPrinters.length && props.salesOrder.company.toLowerCase().includes("amazon");
    const enableChewyLabelPrinting = props.asinLabelPrinters.length && props.salesOrder.company.toLowerCase().includes("chewy");
    const enableWalmartLabelPrinting = props.asinLabelPrinters.length && (props.salesOrder.company.toLowerCase().includes("walmart") || props.salesOrder.company.toLowerCase().includes("furhaven"));


    return (
        <Box sx={{width: "90%"}} className={props.isMobile ? "mobile-table-box" : "desktop-table-box"}>
            <Card sx={{ height: 160, width: 400, display: "inline-block", margin: 2 }}>
                <CardContent>
                    <Box sx={{ p: "20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -2.5, mb: -3.8 }}>
                        <Typography gutterBottom variant="h5" component="div">Sales Order #{props.salesOrder.salesOrderNumber}</Typography>
                    </Box>
                    <Box sx={{ p: "20px", width: "100%" }}>
                        <List dense>
                            <ListItem disableGutters sx={listItemStyles}>
                                <ListItemText sx={listItemTextStyles} primary="Company" secondary={props.salesOrder.company} />
                            </ListItem>
                            <ListItem disableGutters sx={listItemStyles}>
                                <ListItemText sx={listItemTextStyles} primary="External PO number" secondary={props.salesOrder.external_po_number} />
                            </ListItem>
                        </List>
                    </Box>
                </CardContent>
            </Card>
            <div style={{ display: "inline-block", verticalAlign: "top", marginTop: 10, marginLeft: 10 }}>
                <FormGroup>
                    <FormControlLabel label="Hang tag" control={<Checkbox checked={printHangTags} onChange={event => setPrintHangTags(event.target.checked)} disabled={!enableHangTagPrinting} />}/>
                    <FormControlLabel label="ASIN label" control={<Checkbox checked={printAsinLabels} onChange={event => setPrintAsinLabels(event.target.checked)} disabled={!enableAsinLabelPrinting}/>}/>
                    <FormControlLabel label="Chewy label" control={<Checkbox checked={printChewyLabels} onChange={event => setPrintChewyLabels(event.target.checked)} disabled={!enableChewyLabelPrinting} />}/>
                    <FormControlLabel label="Walmart label" control={<Checkbox checked={printWalmartLabels} onChange={event => setPrintWalmartLabels(event.target.checked)} disabled={!enableWalmartLabelPrinting} />} />
                </FormGroup>
            </div>
            
            <Button sx={{ width: 100, margin: 0.5, marginTop: 15, verticalAlign: "top", float: "right" }}
                color="success" variant="contained" onClick={print}>Print</Button>
            <Button sx={{ width: 110, margin: 0.5, marginTop: 15, verticalAlign: "top", float: "right" }}
                color="primary" variant="contained" onClick={zeroAllQuantities}>Zero All</Button>

            <DataGrid
                style={{ width: "100%", marginLeft: 14 }}
                rows={salesOrderItems}
                className={props.isMobile ? "mobile-table" : "desktop-table"}
                columns={itemTableColumns}
                rowsPerPageOption={[25, 50, 100]}
                checkboxSelection={false}
                getRowClassName={getRowClassName}
            />
        </Box>
    );
};

export default SalesOrderDisplay;
